import { useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";

import {
  queryKeys,
  usePreviewTargetsQuery,
  type LifecycleActionVerdict,
} from "@/api/hooks";
import {
  classifyRetargetFailure,
  useChangePreviewTargetMutation,
  type RetargetFailure,
} from "@/api/preview";
import { useCapability } from "@/auth/SessionContext";
import { lifecycleRefusalCopy } from "@/lib/lifecycle";
import { Button } from "@/ui/Button";
import { FieldError } from "@/ui/FieldError";
import { Field, Select } from "@/ui/Input";

import {
  PREVIEW_TARGET_COPY,
  PREVIEW_TARGET_NOT_ENABLED_COPY,
} from "./PreviewTargetNotice";

/**
 * The `preview.configure`-gated retarget control (#338, slice 4/4 of #8).
 *
 * OWNERSHIP. #203 used to own this control and no longer does: the 2026-08-11
 * amendment moved every *Preview as a patient* requirement into #8's slices, and
 * @yanai_Mentaily's 2026-08-13 ruling made the retarget its own slice rather than
 * folding it into #262. Two comments in this tree still named #203 as the owner
 * of a control nobody was building; #338 corrects them and builds it.
 *
 * ABSENT, NOT DISABLED, when the capability is missing — and this is the one
 * place in the application where that is the right posture rather than the usual
 * one. ADR 0001 has controls state their refusal instead of vanishing, and every
 * other gate here follows it: `CandidateFreezeControl` renders disabled with the
 * gate's own reason on it. This does not, because the thing being withheld is
 * not the ACTION but the LIST — a disabled picker still has to render its
 * options to be a picker, and those options are preview-target identifiers. #203's
 * standing invariant (`src/invariants/previewEgress.test.tsx`) forbids this
 * surface from offering a caller any control that could address a target, and it
 * reads the controls the surface actually rendered rather than the code that
 * built them. A caller holding `preview.create` alone must therefore see nothing
 * here at all, which is also exactly what #338's second acceptance criterion
 * asks for.
 *
 * THE 409, and what #262 added to it. #338 surfaced the refusal plainly and
 * stopped; this control now also NAMES the change — the revision the submit was
 * decided against, and the revision the service reports instead — which is the
 * fact a clinician needs before choosing again. What it still refuses to do is
 * decide for them: no target is proposed, nothing is prefilled, and the cleared
 * picker plus the form's own "choose a target" guard mean the next attempt
 * carries a choice made against the state the service actually holds.
 *
 * WHY THIS FORM GROWS NO RECOVERY BUTTON, while its sibling did. #262's rule is
 * one working control per refusal something can change, and this form already IS
 * that control: the picker is refetched and the choice cleared on the two
 * refusals about a value, so choosing again is the recovery. The submit is
 * disabled for exactly the two refusals no choice can answer — a denial and a
 * deployment that does not serve the route — so nothing here offers an action
 * its own sentence says does not exist.
 *
 * It does not clear the selected test patient either: the revision effect in
 * `ProjectPreviewRoute` already does that whenever the revision moves, and a
 * second clearing mechanism would be a defect rather than belt and braces.
 */
export function PreviewTargetControl({
  projectId,
  revision,
  verdict,
}: {
  projectId: string;
  /**
   * The revision this surface DISPLAYED, sent verbatim as the compare-and-set
   * token. Passed in rather than read from cache here for the reason the launch
   * control is given the same value: a control that re-resolved it would
   * re-state a revision that had moved under the clinician, and the 409 that
   * exists to catch exactly that would never fire.
   */
  revision: number;
  /**
   * #650's answer for this exact Project and caller. The session capability
   * still protects target identifiers before the Overview resolves; once the
   * lifecycle is available, this verdict is the action gate and carries an
   * archive or newly-revoked authorization refusal without another policy
   * table in the browser.
   */
  verdict: LifecycleActionVerdict;
}) {
  const { allowed } = useCapability("preview.configure");
  if (!allowed) {
    return null;
  }
  if (verdict.state === "refused") {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        {lifecycleRefusalCopy(verdict.reason)}
      </p>
    );
  }
  return <RetargetForm projectId={projectId} revision={revision} />;
}

/**
 * One sentence per refusal that is NOT about the chosen value.
 *
 * `preview_target` is deliberately absent: the contract names `previewTenantId`
 * on that 422, so it is rendered against the field rather than as a notice about
 * the request. `satisfies` over the remaining union is the exhaustiveness guard —
 * a new kind fails the build instead of rendering blank.
 */
const RETARGET_FAILURE_COPY = {
  // Says what the page DID, because it did it — and claims nothing it cannot
  // stand behind. The re-read of the project is started here, not awaited, and
  // may still be in flight or may have failed outright; a sentence stating it
  // "have been read again" as settled fact is therefore false on screen for as
  // long as either is true. What IS unconditionally true is that the choice was
  // cleared, so the copy asserts that and points at the display rather than
  // vouching for it.
  stale_target:
    "Somebody else changed this project's preview target while this page was open, so this change was refused rather than overwriting theirs. Your choice was cleared: check the target and revision shown above, and choose again if you still want to change it.",
  denied:
    'You are not authorized to change this project\'s preview target. Ask a workspace administrator for the "preview.configure" permission.',
  // Retired the same 501 sentence its sibling control carried: the retarget
  // route is live, and a browser served by a deployment older than its own
  // contract is the one case left where this branch fires. What stays true in
  // that case is that the deployment does not serve the route, which is not
  // something a click here can change.
  unavailable:
    "This deployment of the Editor Service does not serve preview-target changes. That is a property of the deployment rather than of the target chosen, so there is nothing here to retry.",
  error:
    "The Editor Service did not confirm a change of preview target. Nothing about this project's target has been assumed in either direction.",
} satisfies Record<Exclude<RetargetFailure["kind"], "preview_target">, string>;

function RetargetForm({
  projectId,
  revision,
}: {
  projectId: string;
  revision: number;
}) {
  // Read here as well as on the surface around it, and that is one query rather
  // than two: `usePreviewTargetsQuery` is keyed, so both callers share a cache
  // entry. What it buys is that the listing is fetched when the CHOICE is on
  // screen — `staleTime: 0` makes mounting this control a fresh read — which is
  // the contract's own instruction, since the list is derived from provisioned
  // credentials and changes without a deployment.
  const targets = usePreviewTargetsQuery();
  const change = useChangePreviewTargetMutation(projectId);
  const queryClient = useQueryClient();

  const [previewTenantId, setPreviewTenantId] = useState("");
  const [requiredError, setRequiredError] = useState<string | null>(null);
  /**
   * The revision the last submit was decided against (#262).
   *
   * Snapshotted rather than read from the `revision` prop when the refusal
   * renders: this form's own 409 handler invalidates the project, the route
   * passes the live revision straight back down, and this form is deliberately
   * NOT keyed on it — so the prop can already hold the new value by the time the
   * alert appears. Naming the pair from a prop that has moved would print the
   * same number twice and describe a decision nobody made.
   */
  const [sentRevision, setSentRevision] = useState<number | null>(null);
  const errorId = useId();

  const targetItems: string[] = targets.data?.items ?? [];

  /*
   * `isError` and `data` are NOT mutually exclusive — a failed refetch keeps the
   * last good list — so the error arm is gated on there being nothing to fall
   * back on, exactly as `NewProjectDialog` and the notice above this control
   * gate their own. Claiming "not provisioned" from a failed request would turn
   * a transient blip into a statement about the deployment.
   *
   * The two settled sentences are `PreviewTargetNotice`'s own constants rather
   * than new wordings: an empty listing means the same thing here as it does
   * eight pixels above, and one situation with two sentences is how a reader
   * starts wondering which of them is the real one.
   */
  const unavailableReason = targets.isPending
    ? "Loading the preview targets you can choose from."
    : targetItems.length === 0
      ? targets.isError
        ? PREVIEW_TARGET_COPY.unchecked
        : PREVIEW_TARGET_COPY.none_provisioned
      : null;

  // The refetch removes the revoked OPTION; this removes the revoked VALUE. A
  // selection that has left the list is DERIVED away rather than synced back
  // into state with an effect, for the reason `NewProjectDialog` records: the
  // stored id is not a second source of truth, it is only meaningful against the
  // current list.
  const selectedTarget = targetItems.includes(previewTenantId)
    ? previewTenantId
    : "";

  const failure = change.isError ? classifyRetargetFailure(change.error) : null;
  // The service named `previewTenantId`, so the form honours that rather than
  // showing a message about "the request" beside an unmarked control (#338's
  // seventh acceptance criterion).
  const refusedValueError =
    failure?.kind === "preview_target" ? PREVIEW_TARGET_NOT_ENABLED_COPY : null;
  const targetError = requiredError ?? refusedValueError;
  const terminal =
    failure && failure.kind !== "preview_target" ? failure : null;
  /*
   * The two refusals no choice can answer.
   *
   * `denied` is about the CALLER and `unavailable` about the DEPLOYMENT —
   * neither is a statement about the target that was picked, so picking a
   * different one produces the identical 403 or 501. The copy says so out loud
   * ("there is nothing to retry"), and until now the submit stayed live and
   * would resend on a click, which made the screen contradict its own sentence.
   *
   * `error` is deliberately NOT here: an unclassified or transport failure is
   * exactly the case where trying again is the reasonable thing to do.
   *
   * Recovering from either needs something this page cannot do — a capability
   * granted, or the endpoint implemented — so the form stays inert until the
   * session is re-established, rather than pretending a reload of its own would
   * help.
   */
  const unretryable =
    terminal !== null &&
    (terminal.kind === "denied" || terminal.kind === "unavailable");

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (change.isPending) {
      return;
    }
    if (selectedTarget === "") {
      setRequiredError("Choose a preview target.");
      return;
    }

    // The value that goes on the wire, remembered as the value it was. Set
    // before the mutation so the refusal — which can render on the very next
    // pass — always has it.
    setSentRevision(revision);
    change.mutate(
      {
        previewTenantId: selectedTarget,
        // The revision the surface displayed, verbatim. Nothing here increments
        // it, and nothing here re-reads it.
        expectedPreviewTargetRevision: revision,
      },
      {
        onError(error) {
          const refusal = classifyRetargetFailure(error);
          // The listing is now known to be stale — it offered a target the
          // service refuses. Refetching is the only thing that makes the retry
          // the clinician is being asked for a DIFFERENT request, and it is the
          // same recovery `NewProjectDialog` and `CandidateFreezeControl` run
          // for the identical code.
          //
          // The refused VALUE goes at the same moment, and not only via the
          // listing. `invalidateQueries` is not awaited and cannot be: React
          // Query keeps the previous `data` for the whole of a refetch, and
          // keeps it after a FAILED one too. So between the refusal and the
          // next successful listing the revoked target was still an option,
          // still `selectedTarget`, and the submit re-enabled the moment the
          // mutation settled — one unchanged click resent the identical
          // refused request, which is the "re-fetch before retrying" the
          // contract asks for skipped entirely. If the refetch failed the
          // window never closed, because a non-empty stale list keeps
          // `unavailableReason` null. Clearing here bounds it to the refusal
          // itself, exactly as the 409 path below already does.
          if (refusal.kind === "preview_target") {
            setPreviewTenantId("");
            void queryClient.invalidateQueries({
              queryKey: queryKeys.previewTargets,
            });
          }
          // A 409 is the service saying the project's target is not what this
          // page is displaying. #338 ended here; #262 adds the sentence that
          // names both revisions, and nothing else — the handling below is
          // unchanged, deliberately.
          //
          // Re-reading the project is still right: leaving a number on screen
          // that the service has just called stale is a display defect, and
          // #260's surface exists to state the target truthfully. What was
          // wrong was doing that ALONE. The route feeds the re-read revision
          // straight back into this form's `revision` prop, so a refreshed
          // compare-and-set token arrived under an untouched selection and one
          // unchanged click resent it — the browser performing the contract's
          // "re-read, decide, resend" on the clinician's behalf, which was never
          // #262's to build either (Reviewer, PR #386).
          //
          // So the CHOICE goes with the stale token, and #262 leaves that
          // exactly as it is. What it adds is the missing FACT, in the alert
          // below: the revision this submit was decided against, and the one the
          // service reports instead. Nothing is proposed, nothing is prefilled,
          // and the next attempt still requires a target chosen against the
          // state the service actually holds — a resubmit without one is refused
          // by the form's own "choose a target" guard rather than sent.
          if (refusal.kind === "stale_target") {
            setPreviewTenantId("");
            void queryClient.invalidateQueries({
              queryKey: queryKeys.projectOverview(projectId),
            });
          }
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Field
        label="Change preview target"
        // Through `description`, not a loose paragraph: `Field` only populates
        // `describedBy` when it is given one, so a visible-only sentence is
        // never announced — and the state that most needs announcing is the one
        // where there is nothing to choose.
        description={
          unavailableReason ??
          "Every accepted change moves the revision, including a change to the target this project already has. A test patient chosen against the old target is cleared; the frozen candidate is not, because candidates are target-neutral."
        }
      >
        {({ inputId, describedBy }) => (
          <>
            <Select
              id={inputId}
              value={selectedTarget}
              // `aria-disabled`, not the native attribute, on `Button`'s
              // documented reasoning: native `disabled` removes the control from
              // the tab order, and this is precisely the state whose REASON a
              // screen-reader user needs to reach. With nothing to choose the
              // select carries only the placeholder, so it cannot produce a
              // value anyway, and the submit is gated separately.
              aria-disabled={unavailableReason !== null || undefined}
              onChange={(event) => {
                setPreviewTenantId(event.target.value);
                setRequiredError(null);
                // The target the service refused is no longer the one selected,
                // so its rejection stops applying — and a confirmation of the
                // LAST change stops describing the choice now on screen.
                //
                // Except for the two refusals that were never about the value:
                // clearing those on a fresh selection would re-enable the
                // submit and hand back the retry the alert says does not
                // exist, which is the same defect by a longer route. They are
                // cleared by a new session, not by a new choice.
                if ((change.isError && !unretryable) || change.isSuccess) {
                  change.reset();
                }
              }}
              aria-invalid={targetError ? true : undefined}
              aria-describedby={
                [describedBy, targetError ? errorId : null]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
            >
              <option value="">Choose a preview target</option>
              {targetItems.map((target) => (
                <option key={target} value={target}>
                  {target}
                </option>
              ))}
            </Select>
            {targetError ? (
              <FieldError id={errorId} message={targetError} />
            ) : null}
          </>
        )}
      </Field>

      <Button
        type="submit"
        variant="outline"
        // Also disabled when nothing can be chosen: without it the form accepts
        // a submit it then refuses with "Choose a preview target." — an
        // instruction the clinician cannot follow, because the control that
        // would satisfy it is inert. And disabled for the two refusals no
        // choice can answer, so the button stops offering the retry the alert
        // beneath it says does not exist.
        disabled={
          change.isPending || unavailableReason !== null || unretryable
        }
      >
        {change.isPending ? "Changing…" : "Change preview target"}
      </Button>

      {terminal ? (
        <div
          role="alert"
          className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-foreground"
        >
          <p>{RETARGET_FAILURE_COPY[terminal.kind]}</p>
          {/*
            NAMING WHAT CHANGED — this surface's half of #262's guided recovery.
            The launch control does the same for the session 409, and neither
            proposes anything: the pair of revisions is a fact the clinician
            needs in order to decide, not a decision made on their behalf.

            The sent revision is read from `sentRevision`, NOT from the
            `revision` prop, and that distinction is the whole correctness of
            this sentence. The 409 handler invalidates the project, the route
            feeds this form the live revision, and this form is not keyed on it —
            so by the time the refusal renders, the prop may already hold the
            NEW value and the sentence would print one number twice. The
            snapshot is taken at submit, which is when the decision was made.

            What the sentence must not claim is a "before and after": the service
            reports its CURRENT revision, and by the time it is read somebody may
            have moved it again.

            `currentRevision` stays optional because the envelope is data. A 409
            without the field still refuses this change, and the sentence then
            omits what it does not have rather than inventing it.
          */}
          {terminal.kind === "stale_target" ? (
            <p>
              {`This change was decided against revision ${sentRevision ?? revision}.`}
              {terminal.currentRevision === undefined
                ? " The Editor Service reports a different revision now."
                : ` The Editor Service now reports revision ${terminal.currentRevision}.`}
            </p>
          ) : null}
          {/*
            The server's own message is never rendered — #7's rule, and the same
            rule here: a machine-readable code and a request id are what support
            needs, and an unclassified 500 body is what must not reach a
            clinician.
          */}
          {terminal.kind === "error" ? (
            <p className="mt-2 font-mono text-xs text-muted-foreground">
              {terminal.code}
              {terminal.requestId ? ` · request ${terminal.requestId}` : ""}
            </p>
          ) : null}
        </div>
      ) : null}

      {change.isSuccess ? (
        <p
          role="status"
          className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground"
        >
          Preview target changed. The target and revision shown above are the
          ones the Editor Service returned for this project.
        </p>
      ) : null}
    </form>
  );
}
